import { pairKey } from './open-fan';
import { THEIR_LABEL_ZOOMS, labelWeightsFor } from './node-labels';
import { THEIR_STAR_ZOOMS } from './stars';
import { THEIR_WEDGE_ZOOMS } from './wedges';
import { THEIR_ARRIVAL } from './static-frame';
import { THEIR_NEED, THEIR_NODE_ZOOMS } from './nodes';
import type { RenderEdge, RenderNode } from '@versesverse/core';
import type { Box } from './labels';
import type { MapNode } from './nodes';
import type { WebEdge } from './web';
import { clumpRadius } from './wash';
import type { WashClump } from './wash';
import type { Wedge } from './wedges';
import type { StaticScene } from './static-frame';
import type { LiveScene } from './live-frame';
import { NEAR_DUST_SEED, buildNearDust } from './live-frame';
import { FAR_STAR_SEED, buildFarStars } from './far-stars';
import type { FarStar } from './far-stars';
import { mulberry32 } from './rng';
import { buildStars } from './stars';
import { SHAPE_BY_TYPE } from '@versesverse/core';

/*
 * OUR WORLD, IN THE SHAPE THEIR ENGINE ASKS FOR.
 *
 * The port takes NO data: every world dimension is an argument with no default, which is what has
 * kept eighteen modules honest. This is the one file where that bill comes due — every one of those
 * arguments has to be answered, from our roster, and each answer is a decision worth stating.
 *
 * THE SINGLE MOST IMPORTANT THING HERE IS THE RIM. A zoom is pixels per world unit, so every
 * absolute zoom in their engine is a fact about a world 12,320 units across. Ours is whatever our
 * layout settled to. Copy their 0.14 into a world half the size and every layer switches on at the
 * wrong distance — which is exactly how the first port kept the wash on through the whole opening
 * flight and paid twenty-nine screens of overdraw a frame for it. So every threshold below is
 * written as THEIR number and rescaled by the ratio of the rims, and the rescaling is the point.
 *
 * WHAT WE DO NOT HAVE YET, stated rather than faked:
 *
 *   the streamed sky   their 9,700 filler names arrive as tiles and we have no tile build, so the
 *                      streamed FIELD is empty — `0 / 0 chunks · 0 KB` on the readout, always.
 *
 *                      This used to end "and the sky layers draw nothing", which is not so and is
 *                      worth the correction: theirs splits its work by SET — 300 resident, 9,700
 *                      sky — and ours by RANGE, so our sky renderer draws whatever is in range,
 *                      resident or not. Measured with no mocks at the whole field: `1,099 sky`,
 *                      `0 disc · 1099 dot · 0 clump`, the entire roster through that layer. What is
 *                      missing is the filler, not the layer.
 *   readers            the presence plane is not connected here, so populations are zero and the
 *                      seats, the voice rings and the chips draw nothing. CHECKED and still true,
 *                      2026-08-22: with no mocks the readout holds `seats 0 · readers 0` at every
 *                      zoom. The other two entries in this list were audited the same day and both
 *                      had gone stale, so this one carries its date.
 *   passages           NOTHING. This used to say the citations reached only the bundle door, and
 *                      that was wrong: the refs come from `bundle.ties`, which BOTH doors fetch, so
 *                      the streamed door writes the same six. Measured through a real chunked build
 *                      with the skeleton confirmed fetched — see the note in `App.tsx`.
 *
 * None of those are stubs inside the engine — they are empty arguments to it, which is the whole
 * reason the engine takes arguments.
 *
 * AND THE SKY IS WHY A READER ARRIVES WITH FAR FEWER AROUND THEM THAN THEIRS — which looks exactly
 * like a defect and is not. (The heading said "ALONE" until the numbers below stopped supporting it.)
 *
 * Opening a name flies to `max(2.3, z)` in their world (engine.js:817) and to the same FRACTION of
 * ours. At that framing theirs draws 137 names and 70 strands around the one you opened.
 *
 * OURS NO LONGER DRAWS ONE, and the sentence that said so is kept because it is how the paragraph
 * below came to be believed. It read: *"ours draws ONE — and not just for one figure. Measured
 * across all 1,099 on 2026-08-20: the tenth percentile, the median and the ninetieth are all 1, and
 * the most anybody lands beside is 2."* True when written, and describing a layout this build has
 * not had since `stories.ts` and `tiers.ts` began seating names by the story they are in.
 *
 * Re-measured 2026-08-22 over the same roster, counting names inside the arrival frame:
 *
 *   |                      | p10 | median | p90 | most | alone |
 *   |----------------------|-----|--------|-----|------|-------|
 *   | before the story springs |   0 |      2 |   8 |   12 |   202 |
 *   | after them               |   0 |      3 |   9 |   14 |   223 |
 *
 * In a browser, Abraham opens beside SIX with 8 strands and 5 labels. The springs gather a story, so
 * opening a name now shows the names it is cited with — and more names are alone as well, which is
 * the colonies working rather than a contradiction.
 *
 * (The table is geometry over the layout, ignoring tilt and the draw thresholds, so it is a
 * before-and-after rather than a prediction of what a frame holds. The browser is the authority for
 * that, and it says six.)
 *
 * Two hypotheses were tried and both were wrong, which is why they are written here rather than
 * fixed. The arrival zoom is a faithful port — the same share of the world on both. And the layout's
 * disc radii, suspected of holding names apart through `separateOverlaps`, do nothing: re-running
 * the real settle at a half, a quarter, an eighth and a sixteenth of them moved the rim not at all,
 * the ladder not at all, and names-in-frame not at all.
 *
 * What is true is the sky. Their 300 RESIDENT names sit a median 1.59% of the rim apart and ours
 * sit 3.29% apart — twice as sparse, which is real but small. Their other 9,700 are the sky, and
 * the sky is what fills a close view. So this closes when the tile build does, and a session that
 * finds the arrival empty should not go looking for a threshold.
 */

/**
 * HOW WIDE THE FRAME IS AT THE ZOOM PASSAGES APPEAR, as a share of the rim — measured, not chosen.
 *
 * Swept over every zoom with Abraham open (`open-fan.ts` carries the sweep): the frame at
 * `fanRefsFrom` is 4.9% of the rim across, against a median tie of 27%. It is what tells a tie whose
 * passage can be written at its middle from one whose middle is off screen by construction.
 */
const FRAME_SHARE_AT_REFS = 0.049;

/** THEIR rim, which every threshold below was calibrated against. The one number of theirs here. */
const THEIR_RIM = 12_320;

/**
 * Their absolute zooms, at their rim.
 *
 * Kept together and named, because scattered through twenty call sites they would be impossible to
 * re-derive when our layout changes. Each is divided by the world it was measured in and multiplied
 * by ours — see `atOurRim`.
 */
const THEIR_ZOOMS = {
  webFrom: 0.14,
  webPoolFrom: 0.55,
  webPoolSpan: 0.6,
  washGone: 0.34,
  washSpan: 0.3,
  /* The stars and the wedges are read from their own layers now — see below. Wired by hand here,
     two of the five were numbers their engine uses for something else entirely. */
  /* The node fade is read from `nodes.ts` now — both of these were invented. */

  skyStrandsFrom: 0.18,
  /* Where the four level-of-detail bands begin — see the SkyBands type. Rim-rescaled like every
     other absolute zoom, which lands our whole-field view in the DOTS band as theirs does. */
  skyBandEverything: 0.5,
  skyBandDiscs: 0.22,
  skyBandDots: 0.03,
  travellersFrom: 0.14,
  presenceFrom: 0.62,
  disputedFrom: 0.14,
  nearFrom: 0.7,
  nearSpan: 0.5,
  fanRefsFrom: 0.9,
  chipsFrom: 1.05,
  /* The arrival ramp is read from `static-frame.ts` now — all three of these were made up. */
  /** Where the camera lands on a name the reader may walk to, and where it stops when they may not. */
  arriveAt: 2.3,
  lookAt: 0.5,
  /*
   * THE FOUR THAT DECIDE WHAT THE MAP OPENS ON, and the ones it is most obvious to get wrong.
   *
   * Their 0.30 frames about two fifths of a world 12,320 across. Left unscaled in a world a quarter
   * the size it frames the whole thing and then some — an empty screen with the names in a clot at
   * the middle, which is exactly what the first wiring of this engine showed.
   *
   * THESE WERE SUBSTITUTED FOR ALMOST THE WHOLE OF THIS PORT, and are theirs again as of 2026-08-21.
   * `our-floor.ts` stood here and replaced `floorMapBase` with a zoom measured against the CROWD —
   * the 90th percentile of where names actually are — rather than against the rim. Its reason, in its
   * own words, was that the rescale "put FIVE NAMES on the screen, measured", over 1,099 names in a
   * thin ring. It was a careful decision and it was right when it was made.
   *
   * The ring is gone. `stories.ts` and `tiers.ts` seat names in their text's wedge now, so the
   * sentence describes a layout this build does not have. Re-measured in a browser against the real
   * bundle with NO mock data:
   *
   *   at their floor rescaled to our rim   16 names · 690 sky · 88.7% of the frame lit
   *   at the substituted floor, 0.088       0 names · 1,099 sky · 12.5% lit
   *
   * Theirs at its own floor covers about 82%, so one notch in from where a reader could go, ours was
   * already FULLER than theirs — and the place a reader actually could go was a dim lens of dust in
   * black with nothing named on it. That is the screen this port was called far from theirs, and it
   * was the last thing between the two maps that was ours rather than a bug.
   *
   * (16 is the label BUDGET at that band and not a density; the lit share is the number that
   * discriminates. Both are recorded because one of them cannot be read alone.)
   *
   * The walk still opens the view out — `minZoom` subtracts `√visited × floorPerVisited` and that is
   * unchanged and theirs. What a reader loses is the view of the ENTIRE field at once, on a map that
   * says "the dark is what you have not surveyed" at its widest; theirs never opens that far and
   * treats the floor as somewhere you go. `THEIR_ZOOMS.enter` puts the opening view at twice the
   * floor, which is their own ratio (0.6 over 0.30) and was 15 times it while these were substituted.
   */
  floorHeld: 0.72,
  floorFaintest: 0.036,
  /*
   * THE WALK NO LONGER DECIDES HOW FAR OUT A READER MAY PULL — Ariel, 2026-08-22: *"regarding the
   * zoom thing that prevent full zoom until visiting x amount of nodes, disable it"*.
   *
   * `minZoom` returns `max(whole, mapBase - √visited × perVisited)`. At theirs — 0.30 and 0.030 —
   * a reader who has opened nothing is held at 0.30 and has to open about a hundred names before
   * the subtraction reaches the whole field. That is their design and the paragraph above argues
   * both sides of it; what it means for a reader here is that the widest view of the library is
   * something they earn rather than something they are shown.
   *
   * Zeroed rather than unwired: `mapBase` at 0 makes the second term negative for every visited
   * count, so `max` takes `whole` always and the floor IS the whole field from the first visit.
   * `minZoom` is a faithful port verified line by line against engine.js:296 and is untouched —
   * this is the scene handing it different numbers, which is the seam the port was built with.
   * Restoring their behaviour is putting 0.30 back.
   */
  floorMapBase: 0,
  floorPerVisited: 0.030,
  /** The closest the reader may come. Theirs, and a zoom like every other. */
  zoomMax: 5,
  /**
   * WHERE THE MAP OPENS FOR A READER WHO IS ALREADY PAST THE ARRIVAL — theirs, engine.js:354,
   * `z0 = max(minZoom(), 0.6)`.
   *
   * An ABSOLUTE zoom, rescaled like `zoomMax` and unlike a flight, and the distinction is the whole
   * point. Our floor is not theirs: `our-floor.ts` measures it against the CROWD — the 90th
   * percentile of where names actually are — where theirs measures it against the rim and then
   * lifts it by how little the reader has surveyed. So "one floor out" means a different picture on
   * each side, and any multiple of it is unportable. The rim rescale is what carries a picture.
   *
   * Measured: opening at the floor put the reader at z 0.088 with the hint reading "the dark is what
   * you have not surveyed" — theirs says "tap a name to open its verses" there, because on their map
   * the floor is somewhere you GO to see what you have not surveyed, never where you begin. This
   * lands at 1.484, where our own label placer writes 9 names against their 6. The floor multiple
   * that our arrival descends to, 4, puts 90 names on screen.
   */
  enter: 0.6,
} as const;

/** Their world radii, likewise — a distance in a world, so it scales the other way. */
const THEIR_WORLD = {
  haloRadius: 12_320,
  /* engine.js:518 — their sky is a disc of this radius, a QUARTER of their rim. */
  starExtent: 3_400,
  coreRadius: 300,
  webAmbientMax: 950,
  skyEdgeAmbientMax: 900,
} as const;

/** A zoom of theirs, read in our world. Their world was bigger, so our zooms are bigger. */
/**
 * WHERE THE MAP OPENS, in our units — see `THEIR_ZOOMS.enter`.
 *
 * Exported on its own because the app issues this flight, and the app has the rim; everything else
 * in this file is read out of a built scene.
 */
export function enterZoom(rim: number): number {
  return atOurRim(THEIR_ZOOMS.enter, rim);
}

function atOurRim(theirs: number, rim: number): number {
  return theirs * (THEIR_RIM / rim);
}

/** A distance of theirs, read in our world. */
/**
 * HOW WIDE A MARK IN THE FAR FIELD IS, in their world — the range their sky filler is drawn from,
 * and the one thing column 8 of a tile row is allowed to mean.
 *
 * Theirs draws every sky node as `max(0.9, min(46, r × z))` and every one of its sky nodes is filler
 * with a radius from this range. Ours puts the LIBRARY in the sky as well, and was writing each
 * figure's RESIDENT radius there — the radius that makes a name a ring you can aim at when you are
 * close. Two quantities in one column: the frontier's marks came out theirs' size and the real ones
 * came out several times larger, which is what made the far field a rash of spots rather than a
 * field behind the wash.
 */
export const THEIR_SKY_R = { min: 1.71, max: 20 } as const;

/**
 * A figure's radius in the far field, from its weight — the same range the frontier is sampled from,
 * so one column means one thing.
 *
 * Theirs samples its filler's radius independently of weight because its filler is nobody. Ours are
 * real, so the heaviest name is the widest mark, which is the same claim the resident layer makes at
 * close range and the same one the label budget makes about which names are written.
 */
export function skyRadius(weight: number, topWeight: number, rim: number): number {
  const span = Math.max(1, topWeight - 1);
  const share = Math.min(1, Math.max(0, (weight - 1) / span));
  return spanAtOurRim(THEIR_SKY_R.min + share * (THEIR_SKY_R.max - THEIR_SKY_R.min), rim);
}

export function spanAtOurRim(theirs: number, rim: number): number {
  return theirs * (rim / THEIR_RIM);
}

/*
 * THE TABLE ABOVE IS A SECOND COPY, AND HAS BEEN WRONG THREE TIMES.
 *
 * `labelsFrom: 0.09` appears nowhere in their engine. `starsBright: 0.9` is their label weight-gate
 * from line 1693, not a star threshold — theirs is 0.85, on lines 1479 and 1497. `wedgesUntil: 0.2`
 * and `wedgesSpan: 0.12` are neither of the two numbers their petal gate uses, which are 0.13 and
 * 0.04 on line 1643.
 *
 * Every time, the layer's own TEST held the right value — checked against the engine line by line —
 * a few files away from a wiring that had made one up. So a layer that knows its own thresholds now
 * exports them, its test reads them, and the scene rescales them at the point of use. One copy, and
 * the copy is the one that was checked.
 *
 * Rescaled field by field rather than by a helper over the whole object: a generic that rebuilds an
 * interface needs a cast at the end, and this port does not use them. Naming each field also means
 * a threshold ADDED to a layer will not silently arrive here unrescaled.
 */

/** How the reader's own view of the map is set, which the caller owns rather than this. */
export interface OurLook {
  /** How much of the star field they want, and how many names they want written. */
  starDensity: number;
  labelDensity: number;
  /** Which corpora and kinds they have switched off, keyed `c_<key>` and `k_<kind>`. */
  off: Readonly<Record<string, boolean | undefined>>;
  /** Where the interface covers the map — measured, never guessed. */
  chrome: readonly Box[];
}

/**
 * A figure as every layer of this engine wants it.
 *
 * `MapNode` is what the still picture reads; the moving layers want three more — who is here, who
 * is speaking, and whether the traditions disagree about who this is. One type rather than two
 * parallel arrays, because the alternative is two arrays that can fall out of step.
 */
export type OurNode = MapNode & { voice: boolean; disputed: boolean };

/** Everything derived from a roster once, and reused until the roster itself changes. */
export interface OurWorld {
  /** The roster in their shape, in the same order as the array it came from. */
  nodes: OurNode[];
  edges: WebEdge[];
  /** A name's own ties, for the fans. */
  edgesOf: (id: number) => readonly number[];
  /** Where the naming stops, which everything else is measured against. */
  rim: number;
  /** The world's vertical scale. One number for the whole scene. */
  squash: number;
  /** Corpus key → colour, and the order the keys are packed in. */
  palette: Record<string, string>;
  corpusKeys: string[];
  /** Our corpus names, by the single-character key this engine addresses them with. */
  corpusNames: Record<string, string>;
  /** And the way back: our corpus id to that key. What `engineOff` needs, and nothing else. */
  keyOf: ReadonlyMap<string, string>;
  /** How many traditions must name a figure before it counts as shared. */
  sharedAtLeast: number;
  /** Which traditions each corpus KEY belongs to — what makes that a threshold on traditions. */
  traditionsOfKey: Readonly<Record<string, readonly string[]>>;
  /** The rungs, outward. */
  rungs: { r: number }[];
  wedges: Wedge[];
  /** Where each scripture's names gather, which the wash is painted from. */
  clumps: Record<string, WashClump[]>;
  /** The closest the reader may come, rescaled to our world. */
  zoomMax: number;
  /** The heaviest weight in the roster, where the label need-curve bottoms out. */
  topWeight: number;
  /**
   * HOW FAR THE FIELD REACHES, squashed the way the view squashes it — the furthest name drawn.
   *
   * The camera's whole-field floor is a fraction of this, as theirs is a fraction of `RIM`.
   *
   * THIS USED TO BE THE 90TH PERCENTILE, and the reason is instructive because it has expired. The
   * settle did not converge, so a dozen names caught mid-flight put the furthest at 3,747 against a
   * crowd stopping at 1,401 — the rim was half again beyond anything a reader would look at, and a
   * camera fitted to it framed mostly empty space. Measuring the crowd instead was the right answer
   * to that. With the settle converged the ratio is 1.15, so the crowd IS the field and the
   * deviation only crops the view.
   *
   * Fitting the reach puts our field at 885 of 1,600 screen pixels; theirs occupies 887 of the same
   * frame. Fitting the 90th percentile put ours at 1,049 — noticeably larger than theirs, which is
   * how it was spotted.
   *
   * AND THE TWO ROUTES NOW AGREE, which is the part worth trusting. The floor is
   * `max(baseFloor, faintest)`: one is this reach fitted to the viewport, the other is their 0.036
   * rescaled by the rims — a fit and a constant, arrived at from opposite directions. They come to
   * 0.277 and 0.281. Theirs does the same thing and lands on 0.0355 against 0.036. Before this they
   * were 0.333 and 0.281, and the fit was simply overriding the constant.
   */
  reach: number;
}

/**
 * ONE CHARACTER PER CORPUS.
 *
 * Their engine addresses a scripture by a single character, because a node's whole membership is
 * one short string and a bitmask is packed from its index. Ours are named things — `HB`, `QURAN`,
 * `MISHNEH_TORAH` — so the adapter assigns each a character and keeps the map both ways. Assigned
 * from the ROSTER's own order rather than from a written list, so a corpus added upstream arrives
 * here without anything being retyped.
 */
const KEY_ALPHABET = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';

/**
 * HOW MANY STARS, AND HOW FAR THEY REACH — engine.js:516-520, and both were ours and both wrong.
 *
 *     const a = rnd() * Math.PI * 2, rr = Math.pow(rnd(), 0.55) * 3400;
 *     stars[i*3] = Math.cos(a) * rr; stars[i*3+1] = Math.sin(a) * rr * 0.86;
 *
 * Sixteen thousand in a DISC of 3,400 — which is 28% of their rim, biased outward and squashed
 * into the world's plane. Their sky is a dense core, not a backdrop.
 *
 * OURS SCATTERED ELEVEN THOUSAND OVER A SQUARE of 2.6 rim — nine times wider in each direction,
 * no bias, no squash — on the reasoning written here at the time: "they are the sky, so they are
 * outside everything". A defensible sentence and not their picture. At the floor it put about
 * 2,580 stars on screen where theirs puts 16,000, and spread them evenly where theirs gathers
 * them. Measured across the same row of the same frame, their field runs from luminance 20 to 40
 * and ours from 11 to 17.
 *
 * The extent is a WORLD DIMENSION and scales with the rim; the count is a density over the disc it
 * fills, so it does not.
 */
const STAR_COUNT = 16_000;
const STAR_SEED = 1337;

/**
 * HOW COARSE THE WASH IS, AND HOW MUCH OF IT THERE IS — a grid this many cells across the world,
 * and this many of its cells kept per scripture.
 *
 * Both are ours to set: they turn a POPULATION into a visual, and theirs are facts about ten
 * thousand names where ours have to be facts about eleven hundred. What is NOT ours are the four
 * measures they are set against, all read off their eighty baked blobs:
 *
 *   disc painted  how much of the whole disc the layer covers. THIS IS THE ONE A READER SEES,
 *                 and it is the one this note did not have. Theirs is 25%.
 *   crowd held    how much of a scripture's crowd the kept clumps contain — whether the layer
 *                 describes the crowd or twenty arbitrary spots. Theirs is 100%.
 *   r / gap       a clump's radius against the distance to the nearest other of the SAME text.
 *                 Theirs runs 1.60 to 2.34 — every blob overlaps its neighbours, which is what
 *                 turns eighty blobs into four clouds. Measured across all texts at once it
 *                 comes out 21 to 105, because a clump of one text sits on a clump of another.
 *   r / rim       the mean clump's radius as a share of the world. Theirs is 16%.
 *
 * MEASURED over the real roster, 2026-08-20, after names were seated by STORY:
 *
 * | grid · keep | clumps | r/rim | r/gap | crowd held | disc painted |
 * |---|---|---|---|---|---|
 * | theirs | 80 | 16% | 1.60-2.34 | 100% | **25%** |
 * | 10 · 20 | 100 | 26% | 1.51-2.24 | 96-99% | 67% |
 * | 14 · 40 (was shipped) | 188 | 18% | 1.24-2.04 | 98-100% | **67%** |
 * | 14 · 12 | 60 | 19% | 1.58-2.07 | 83-93% | 30% |
 * | **18 · 20** | **100** | **14%** | **1.54-1.98** | **81-93%** | **28%** |
 * | 18 · 12 | 60 | 15% | 1.55-1.90 | 72-89% | 23% |
 * | 22 · 20 | 100 | 12% | 1.38-1.90 | 77-91% | 23% |
 *
 * WHY THE OLD PAIR HAD TO MOVE, AND WHY IT WAS NOT WRONG WHEN IT WAS CHOSEN. 14 · 40 was the only
 * pair inside the three measures this note used to list, and it still is: 18% of the rim, blobs
 * that overlap, and all but nothing of any crowd left out. What it was never asked is how much of
 * the map it PAINTS, and the answer is two thirds of it — against their quarter. A reader sees a
 * coloured mush with the names inside it rather than five clouds with dark between them.
 *
 * "Coverage" appears twice above and means two different things, which is exactly how it was
 * missed: the old note's coverage is CROWD HELD, and it was driven to 98-100% by a pair that also
 * drove the disc to 67%. Both numbers were real; only one is about the picture.
 *
 * THE TWO CANNOT BOTH BE THEIRS, because our crowd is a different shape. Their three hundred names
 * sit in four compact lobes, so eighty blobs hold every one of them and still leave three quarters
 * of the disc dark. Ours are eleven hundred over an annulus with five texts lapping, so holding
 * every name means painting most of the field. 18 · 20 takes the reader's side of that trade: the
 * disc within three points of theirs, and 81-93% of each crowd still inside its own cloud.
 *
 * The note this replaces set the grid against `r / rim` alone and read 12% as agreement with their
 * 12-20%. It was the right number for the wrong reason: at that grid no clump touched another.
 */
const CLUMP_GRID = 18;

/** How many of a scripture's cells become clumps. See the note above for what set it. */
const CLUMPS_PER_SCRIPTURE = 20;

/**
 * HOW BIG A MIDDLING NAME IS, as a share of the rim.
 *
 * Theirs, measured: the median of their three hundred radii is 9.1 in a world 12,320 across, which
 * is about three quarters of a thousandth. Dimensionless, and therefore portable in a way the
 * radius itself is not.
 *
 * OUR LAYOUT'S RADII ARE CHOSEN FOR OUR OWN RENDERER and are about four times that share, which at
 * any zoom draws every name as a blob and the middle of the map as a solid mass. The shape of our
 * distribution is kept — a heavier name is still bigger — and only the scale is set, so this is a
 * calibration rather than a replacement. Found by looking at the map, not by any test.
 */
const MEDIAN_DISC_SHARE = 0.000735;

/**
 * Turn our roster into the world every layer measures against.
 *
 * Called once per roster, not per frame. Everything here is O(n) over the names.
 */
/**
 * WHERE EACH TEXT SITS ON THE WHEEL, as the build worked it out — `Bundle.wheel`.
 *
 * Optional, and preferred when it is there. `wedgesFrom` below derives an answer from the roster and
 * has to, because this engine can be driven from a skeleton that carries no wheel; but the build's
 * is the AUTHORITATIVE one and the other renderer has always used it.
 *
 * It matters more than it looks. `Corpus.wheelOrder` says the map gives a figure's ANGLE a meaning —
 * which texts name it — and that only works if every text owns a direction. A wedge label placed by
 * a different rule from the one that placed the figures is a label pointing at the wrong crowd.
 */
export interface WheelSeat {
  id: string;
  a: number;
  r: number;
  /** How many figures this text alone names — the second line of its label. */
  only: number;
}

export function ourWorld(
  nodes: readonly RenderNode[],
  edges: readonly RenderEdge[],
  corpusColour: Readonly<Record<string, string>>,
  traditionsOf: (corpus: string) => readonly string[],
  wheel?: readonly WheelSeat[],
): OurWorld {
  /* The corpora actually present, in the roster's own order — derived, never retyped. */
  const seen: string[] = [];
  for (const n of nodes) for (const c of n.corpora) if (!seen.includes(c)) seen.push(c);
  const keyOf = new Map<string, string>();
  const corpusNames: Record<string, string> = {};
  const palette: Record<string, string> = {};
  const corpusKeys: string[] = [];
  seen.forEach((corpus, i) => {
    const key = KEY_ALPHABET[i] ?? KEY_ALPHABET[KEY_ALPHABET.length - 1] ?? 'z';
    keyOf.set(corpus, key);
    corpusNames[key] = corpus;
    palette[key] = corpusColour[corpus] ?? '#888888';
    corpusKeys.push(key);
  });

  /* How many traditions the library carries, which is what "shared" has to mean — see
     `RenderNode.shared`, where counting BOOKS rather than traditions is the trap. */
  const traditions = new Set(seen.flatMap(c => traditionsOf(c)));

  /* And the same fact per KEY, because the layers address a corpus by its key and the threshold
     above is a count of traditions. Built here so the two cannot be derived differently. */
  const traditionsOfKey: Record<string, readonly string[]> = {};
  for (const [corpus, key] of keyOf) traditionsOfKey[key] = traditionsOf(corpus);

  let rim = 1;
  let topWeight = 1;
  const mapped: OurNode[] = nodes.map((n, i) => {
    const cs = n.corpora.map(c => keyOf.get(c) ?? '').join('');
    rim = Math.max(rim, Math.hypot(n.x, n.y));
    /* Our weight is how many scriptures name a figure. Theirs is a separate importance number we
       do not carry; this is the nearest honest stand-in, and it is what the label curve reads. */
    const w = Math.max(1, n.corpora.length);
    topWeight = Math.max(topWeight, w);
    return {
      id: i,
      name: n.label,
      x: n.x,
      y: n.y,
      r: n.r,
      w,
      cs,
      dom: cs[0] ?? '',
      kind: n.type,
      /* Their `verified` is ours: a figure nobody has checked is drawn fainter. It read `!n.auto`
         — not MACHINE-FOUND — which drew 208 unchecked hand-entered figures at full strength. */
      verified: n.checked,
      /*
       * WRITTEN BY THE PRESENCE PLANE, a poll at a time — zero until somebody is standing here.
       *
       * Mutable on purpose, and the one thing in this file that is. Rebuilding the whole roster
       * every minute to change two numbers on a handful of figures would throw away the picking
       * grid and the wash texture with it, for a ring of seats.
       */
      pop: 0,
      voice: false,
      /*
       * WHETHER THE TRADITIONS DISAGREE ABOUT WHO THIS IS — the slow pulse in `live-frame.ts`.
       *
       * Ours all along and dropped on the way across, so the layer has been drawing nothing. It is
       * one of the few marks on the map that says something about the SCHOLARSHIP rather than about
       * the reader, and this project exists to show exactly those.
       */
      disputed: n.contested === true,
    };
  });

  /*
   * SET THE DISCS TO THEIR PROPORTION, once the rim is known.
   *
   * A second pass rather than a factor inside the first, because the scale cannot be known until
   * every name has been seen: it is the median radius against the rim, and the rim is the furthest
   * name. Multiplied, so the distribution's shape survives and only its scale is set.
   */
  const sorted = mapped.map(n => n.r).sort((a, b) => a - b);
  const median = sorted[Math.floor(sorted.length / 2)] ?? 0;
  if (median > 0) {
    const scale = (MEDIAN_DISC_SHARE * rim) / median;
    for (const node of mapped) node.r *= scale;
  }

  const ties: WebEdge[] = edges.map(e => [e.a, e.b] as const);
  const byNode = new Map<number, number[]>();
  for (const [a, b] of ties) {
    const at = byNode.get(a) ?? [];
    at.push(b);
    byNode.set(a, at);
    const bt = byNode.get(b) ?? [];
    bt.push(a);
    byNode.set(b, bt);
  }

  /* The rungs the layout was asked for, read off the names rather than written down again — a
     retyped ladder is a second copy nothing keeps in step. Each rung's radius is the median of the
     names placed on it, because the settle pushes individuals off their ring and the median is what
     survives that. */
  const byTier = new Map<number, number[]>();
  nodes.forEach(n => {
    const at = byTier.get(n.tier) ?? [];
    at.push(Math.hypot(n.x, n.y));
    byTier.set(n.tier, at);
  });
  const rungs = [...byTier.keys()].sort((a, b) => a - b).map(tier => {
    const radii = (byTier.get(tier) ?? []).slice().sort((a, b) => a - b);
    return { r: radii[Math.floor(radii.length / 2)] ?? 0 };
  });

  return {
    nodes: mapped,
    edges: ties,
    edgesOf: id => byNode.get(id) ?? [],
    rim,
    squash: 0.86,
    palette,
    corpusKeys,
    corpusNames,
    keyOf,
    sharedAtLeast: traditions.size,
    traditionsOfKey,
    rungs,
    /* The build's wheel where there is one; derived from the roster where there is not. */
    wedges: seatedWedges(wheel, keyOf, mapped) ?? wedgesFrom(mapped, corpusKeys, rim),
    clumps: clumpsFrom(mapped, corpusKeys, rim),
    topWeight,
    zoomMax: atOurRim(THEIR_ZOOMS.zoomMax, rim),
    reach: reachRadius(mapped, 0.86),
  };
}

function reachRadius(nodes: readonly { x: number; y: number }[], squash: number): number {
  if (nodes.length === 0) return 1;
  /* Squashed as the view squashes it: the camera has to fit what is DRAWN, not what is stored. */
  return Math.max(1, ...nodes.map(n => Math.hypot(n.x, n.y * squash)));
}

/**
 * WHERE EACH SCRIPTURE SITS ON THE WHEEL, and how far its names reach.
 *
 * Derived from where its names actually ARE — the mean direction of everything it names, and the
 * distance the bulk of them fall inside. A wedge written down by hand would drift the moment the
 * layout settled differently; this cannot.
 */
/**
 * The wedges as the BUILD seated them, or null where the caller has no wheel to give.
 *
 * Preferred over deriving them, because the wheel is not a summary of where a text's names happen to
 * be — it is what PUT them there. `Corpus.wheelOrder` fixes the order the texts are met in going
 * clockwise, the layout places every figure by which texts name it, and `schema/wheel.ts` sizes each
 * wedge by the crowd facing it. A label placed by re-averaging the result is an approximation of a
 * number that was already exact, and the two renderers would answer differently for one map.
 *
 * `only` comes with it, which is the count under each label — 456 for the Hebrew Bible, 11 for the
 * Quran. Derived here it agreed exactly, which is reassuring and still two implementations.
 */
function seatedWedges(
  wheel: readonly WheelSeat[] | undefined,
  keyOf: ReadonlyMap<string, string>,
  nodes: readonly OurNode[],
): Wedge[] | null {
  if (wheel === undefined || wheel.length === 0) return null;
  const seated: Wedge[] = [];
  for (const seat of wheel) {
    const k = keyOf.get(seat.id);
    /* A text on the wheel that this roster does not carry has no wedge to draw. */
    if (k === undefined) continue;
    /*
     * The wheel carries the EXCLUSIVE count and no total, and the total does not need it: how many
     * names a text holds is a fact about the roster this function was handed. Counted here rather
     * than waited for, so both doors say the same thing.
     */
    let held = 0;
    for (const n of nodes) if (n.cs.includes(k)) held += 1;
    seated.push({ k, label: '', a: seat.a, r: seat.r, count: seat.only, held });
  }
  return seated.length === 0 ? null : seated;
}

/**
 * THE READER’S FILTER, PUT INTO THE ENGINE’S OWN KEYS — and the filter did nothing until it was.
 *
 * The row along the foot of the map emits `c_HB`, `c_QURAN`, `k_person`: our corpus ids, which are
 * the only names the app has. Every layer inside the engine asks `off['c_' + key]` where the key is
 * ONE CHARACTER from `KEY_ALPHABET` — `c_a`, `c_b` — because that is how their engine addresses a
 * scripture and the port did not change it.
 *
 * So the two never met. Putting the Hebrew Bible away dimmed its chip and left all 1,099 names on
 * the canvas, measured: 1,099 dots before, 1,099 after. The filter has been ported, differentially
 * tested, wired to a row of controls, and inert.
 *
 * The translation lives HERE because the alphabet is this adapter’s private business — the same
 * reason `our-scene.test.ts` insists a wedge is named by our corpus and never by the key. A caller
 * that had to do this itself would be a second copy of the mapping.
 *
 * Kind keys pass through untouched: `k_person` is our own `EntityKind`, which is what the engine
 * was given as `kind`.
 */
export function engineOff(
  off: Readonly<Record<string, boolean | undefined>>,
  keyOf: ReadonlyMap<string, string>,
): Record<string, boolean | undefined> {
  const out: Record<string, boolean | undefined> = {};
  for (const [name, value] of Object.entries(off)) {
    if (value !== true) continue;
    if (!name.startsWith('c_')) { out[name] = true; continue; }
    const key = keyOf.get(name.slice(2));
    /* A corpus the world never saw has no key and cannot be hidden — dropping it is the honest
       answer, since inventing one would hide whichever scripture happened to hold that letter. */
    if (key !== undefined) out[`c_${key}`] = true;
  }
  return out;
}

function wedgesFrom(nodes: readonly MapNode[], keys: readonly string[], rim: number): Wedge[] {
  return keys.map(key => {
    const mine = nodes.filter(n => n.cs.includes(key));
    let sx = 0;
    let sy = 0;
    const radii: number[] = [];
    for (const n of mine) {
      const d = Math.hypot(n.x, n.y) || 1;
      sx += n.x / d;
      sy += n.y / d;
      radii.push(d);
    }
    radii.sort((a, b) => a - b);
    /* The eightieth centile rather than the furthest: one stray name should not stretch a wedge
       across the whole field. */
    const reach = radii[Math.floor(radii.length * 0.8)] ?? rim;
    /*
     * THE COUNT IS THE NAMES THIS SCRIPTURE HOLDS ALONE, because that is what the label under it
     * says: "N named here only". It was `mine.length` — every name the scripture carries, shared
     * ones included — so the map read "699 named here only" under the Hebrew Bible when 699 is how
     * many names it holds at all and 456 is how many it holds by itself.
     *
     * Theirs is the same rule from the other end: a node belongs to a petal when `cs.length===1`
     * (engine.js:1290), and the petal counts are what that leaves.
     *
     * Only the COUNT. The angle and the reach still read every name in the scripture, because they
     * answer a different question — where this scripture's names lie, not which of them are its
     * alone — and a petal aimed by its exclusive names only would swing away from its own crowd.
     */
    const alone = mine.reduce((n, node) => n + (node.cs.length === 1 ? 1 : 0), 0);
    return { k: key, label: '', a: Math.atan2(sy, sx), r: reach, count: alone, held: mine.length };
  });
}

/**
 * Where a scripture's names gather, on a coarse grid — what the wash's texture is painted from.
 *
 * The grid and the count are ARGUMENTS with the shipped pair as defaults, because the note above
 * them is a table of settings and a table can only be re-run if the thing it measures takes them.
 * It had to be re-run once already: that table was measured before names were seated by story, and
 * a differently shaped crowd is a different answer.
 */
export function clumpsFrom(
  nodes: readonly MapNode[],
  keys: readonly string[],
  rim: number,
  grid: number = CLUMP_GRID,
  keep: number = CLUMPS_PER_SCRIPTURE,
): Record<string, WashClump[]> {
  const cell = (rim * 2) / grid;
  const out: Record<string, WashClump[]> = {};
  for (const key of keys) {
    const buckets = new Map<string, { x: number; y: number; n: number }>();
    for (const n of nodes) {
      if (!n.cs.includes(key)) continue;
      const k = `${Math.round(n.x / cell)},${Math.round(n.y / cell)}`;
      const at = buckets.get(k) ?? { x: 0, y: 0, n: 0 };
      at.x += n.x;
      at.y += n.y;
      at.n += 1;
      buckets.set(k, at);
    }
    /*
     * THE BEST-POPULATED CELLS ONLY, and this is what makes a wash out of a speckle.
     *
     * Theirs carries exactly twenty clumps a scripture with a median of 45 to 199 names in each.
     * Taking EVERY occupied cell instead gave us nineteen to forty-three with a median of one to
     * four — the counts looked close and the picture did not, because most of ours were a single
     * name caught at the edge of the ring. Our roster is an annulus, so a square grid over it
     * always produces a long tail of one-name cells; they contribute nothing a reader can see and
     * they drag the whole layer down, since `buildWash` needs six names before a clump reaches
     * full strength and the radius rule below reads the same count.
     *
     * Sorted by population and cut at theirs. The number is a look decision about how many blobs
     * read as weather rather than as spots, and it survives our roster growing.
     */
    const kept = [...buckets.values()].sort((p, q) => q.n - p.n).slice(0, keep);
    out[key] = kept.map(b => ({
      x: b.x / b.n,
      y: b.y / b.n,
      /*
       * WIDER THAN THE GAP TO THE NEXT CLUMP, which is the whole of it — see `THEIR_CLUMP_SIZE`.
       *
       * What stood here was `cell * (0.35 + 0.5*sqrt(n)/8)`, described as theirs. Their rule is
       * `1210 + 6.6n` capped at 2970 on a 1,000-unit cell, and the difference is not a detail: the
       * old expression made every clump NARROWER than the cell it came from, so no clump reached
       * its neighbour and the layer drew a hundred separate spots in five colours instead of one
       * cloud per scripture.
       *
       * It had been checked, but against the median radius as a share of the rim — a number ours
       * matched while the picture stayed wrong, because a blob's size only means something next to
       * the distance to the next blob.
       */
      r: clumpRadius(cell, b.n),
      n: b.n,
    }));
  }
  return out;
}

/**
 * The far field, which is what is out there past everything. Screen space, so it takes no rim.
 *
 * WHAT THIS REPLACED, AND THE MEASUREMENT THAT SETTLED IT. Twenty-six clouds of dust used to sit
 * out past the rim, and they were the thing Ariel called "white washed skewed shapes". They were
 * not a bad port of theirs — they were ours, and the sizing was written as a share of the BAND'S
 * WIDTH rather than as a width of its own:
 *
 *     rx = span * (0.18 + random() * 0.3)
 *
 * On a rim of 4,790 that is a mean cloud 736 wide — 15.4% of the rim, against the 0.55% theirs
 * draws, and 0.66 of the distance to the next cloud against their 0.05. Twenty-six soft blobs each
 * a fifth of the map across, overlapping. The look followed from the arithmetic exactly.
 *
 * It is measured here rather than only described because the first measurement was taken against
 * `buildDeepDust` — the faithful port in `void.ts`, which is not what ships — and came back 2.6x
 * rather than 28x. A count is a fact about a container. See `far-stars.ts` for what stands here now.
 */
export function ourFarStars(): FarStar[] {
  return buildFarStars(mulberry32(FAR_STAR_SEED));
}

/** The star field: three numbers a star, packed flat. */
export function ourStars(rim: number, squash: number): Float32Array {
  /* Theirs, through the port that has been sitting in `stars.ts` unused since it was written. */
  return buildStars(
    { extent: spanAtOurRim(THEIR_WORLD.starExtent, rim), count: STAR_COUNT, squash },
    mulberry32(STAR_SEED),
  );
}

/** The near dust, which lives in screen space and is the same field whatever the world is. */
export function ourNearDust(): ReturnType<typeof buildNearDust> {
  return buildNearDust(mulberry32(NEAR_DUST_SEED));
}

/**
 * The still picture's scene: everything that does not change until the roster does.
 *
 * `countLine` and the rung labels are the caller's, because they are WORDS and this app speaks
 * three languages.
 */
/**
 * THE WORDS ON THE MAP, which are the caller's and never this file's.
 *
 * Theirs are English literals in the middle of the drawing code. Ours come from a typed trilingual
 * dictionary, and a scripture's own name is not translated at all — the Qur'an is the Qur'an in
 * every language this app speaks. So both arrive as functions rather than as strings.
 */
export interface OurWords {
  /**
   * What a scripture is called on the wheel, by OUR corpus id — `HB`, `QURAN` — never by the
   * single-character key this engine addresses it with. The key is this file's private business
   * and a caller that had to know it would be a second copy of the alphabet below.
   */
  wedgeLabel: (corpus: string) => string;
  /** How to say "N named here only". */
  countLine: (of: { held?: number; alone: number }) => string;
  /**
   * What a scripture is called IN A SENTENCE — "Hebrew Bible", not the wheel's "HEBREW BIBLE".
   *
   * `wedgeLabel` upper-cases, because a label on the wheel is set in caps. Reusing it for the card
   * under the pointer and the search row printed "Person · HEBREW BIBLE & MISHNAH" in the middle of
   * a line of prose, which is shouting. Two registers, two lookups.
   */
  corpusName: (corpus: string) => string;
  /** What a kind of thing is called — "Person", "Place" — for the card under the pointer. */
  kindName: (kind: string) => string;
  /** How to say "4 corpora", and how to join two scripture names. Both for that same card. */
  corporaCount: (n: number) => string;
  joinNames: (names: readonly string[]) => string;
}

export function ourStaticScene(world: OurWorld, washCorpusOrder: readonly string[], words: OurWords): StaticScene {
  const rim = world.rim;
  const z = (k: keyof typeof THEIR_ZOOMS): number => atOurRim(THEIR_ZOOMS[k], rim);
  return {
    squash: world.squash,
    void: {
      haloRadius: rim,
      squash: world.squash,
    },
    farStars: ourFarStars(),
    rungs: {
      rungs: world.rungs,
      coreRadius: spanAtOurRim(THEIR_WORLD.coreRadius, rim),
      squash: world.squash,
    },
    clouds: {
      /* No tile build yet, so no clouds: an unloaded region cannot be drawn as one when there are
         no regions. Stated as an empty index rather than as a disabled layer. */
      index: [],
      tile: rim / 8,
      alphaPerName: 0.00014,
      squash: world.squash,
    },
    wash: {
      paint: {
        corpusKeys: washCorpusOrder,
        palette: world.palette,
        clumps: world.clumps,
        rim,
        squash: world.squash,
      },
      fade: { gone: z('washGone'), span: z('washSpan') },
    },
    stars: {
      field: ourStars(rim, world.squash),
      zooms: {
        thin: atOurRim(THEIR_STAR_ZOOMS.thin, rim),
        dim: atOurRim(THEIR_STAR_ZOOMS.dim, rim),
        bright: atOurRim(THEIR_STAR_ZOOMS.bright, rim),
      },
    },
    web: {
      world: {
        ambientMax: spanAtOurRim(THEIR_WORLD.webAmbientMax, rim),
        /* Their gate of 4 separates real names from synthetic filler, which we have none of. Ours
           is 1: every tie we hold was put there by the pipeline and deserves drawing. */
        minWeight: 1,
        squash: world.squash,
      },
      zooms: { from: z('webFrom'), poolFrom: z('webPoolFrom'), poolSpan: z('webPoolSpan') },
    },
    sky: {
      paint: {
        corpusKeys: world.corpusKeys,
        palette: world.palette,
        gridCell: rim / 16,
        clumpCell: rim / 5,
        squash: world.squash,
        sharedAtLeast: world.sharedAtLeast,
        traditionsOfKey: world.traditionsOfKey,
      },
      /*
       * OUR SKY IS THE WHOLE LIBRARY AND THEIRS IS FILLER, SO THE DISC BANDS STAY SHUT.
       *
       * Theirs splits the work by SET: 300 real names resident with the full treatment, 9,700
       * synthetic filler in the sky, and no name in both — so its sky may draw discs at any zoom
       * without doubling anything. Every one of ours is real and all of them are in both places:
       * resident for the arcs, the rings and the kinds, and in the field so the far bands have
       * something to draw. Putting the two DISC bands beyond any reachable zoom leaves the sky
       * drawing dots, and a one-pixel dot under a resident ring is nothing.
       *
       * I OPENED THEM AND WAS WRONG, and the way it went wrong is the part worth keeping.
       *
       * The first attempt drew a rash of large opaque spots, and the cause was real: the sky was
       * being handed each figure's RESIDENT radius. `skyRadius` fixed that — the far field has its
       * own width now, from the same range the frontier is sampled from, so column 8 of a tile row
       * means one thing. With the sizes right the numbers moved the right way (ink 5.4 to 8.1
       * against theirs' 29) and I recorded that the doubling was "real and invisible, which is the
       * only kind worth having".
       *
       * It was neither. Ariel saw it immediately: every name had a second mark beside it and only
       * one of them could be clicked, because the sky is not hit-tested. At the opening view that is
       * a solid dot inside each ring and a scatter of orphans where the resident partner had faded —
       * measured 19 sky discs over the same 19 names the resident layer had already drawn.
       *
       * The arithmetic I trusted said a far-field radius is sub-pixel wherever a ring is drawn. It
       * is not: at the entry zoom `skyRadius` gives the heaviest names 4.5px, which is a dot, not a
       * rounding error. A number checked against the wrong quantity again — the radius against the
       * zoom, when what mattered was the radius against the RING it lands inside.
       *
       * So the bands are shut, and the far field keeps its 5.4 ink against theirs' 29. That gap is
       * the honest one: theirs paints 9,700 invented names around a real 300, ours has 1,099 real
       * ones filling the whole crowd. Closing it needs the sky built from the FRONTIER ALONE —
       * theirs' split by set — which is a real change to `residentSky` and its strands, and it is
       * the only thing that makes these two bands openable.
       */
      zooms: {
        strandsFrom: z('skyStrandsFrom'),
        bands: {
          everything: Number.POSITIVE_INFINITY,
          discs: Number.POSITIVE_INFINITY,
          dots: z('skyBandDots'),
        },
      },
      edges: {
        edgeCell: rim / 16,
        squash: world.squash,
        minWeight: { near: 1, far: 2 },
        ambientMax: spanAtOurRim(THEIR_WORLD.skyEdgeAmbientMax, rim),
      },
    },
    nodes: {
      world: {
        depthHalf: rim * 0.21,
        /*
         * WHICH NAMES ARE LIT AT WHICH ZOOM — theirs, and all four of these were wrong.
         *
         * Their curve is (0.17+(10-w)*0.055)*0.25 (engine.js:1580). This wired the BASE from the
         * node fade's `from`, the PER-WEIGHT from its `span`, and a scale of 1 where theirs is a
         * quarter — three unrelated numbers and an invented one, deciding which names a reader
         * sees at every range.
         *
         * `perWeight` is read against our own top weight rather than their ten, the same way the
         * label ladder is: a weight is a count of ties and means nothing without its scale.
         */
        need: {
          base: atOurRim(THEIR_NEED.base, rim),
          perWeight: atOurRim(THEIR_NEED.perWeight * (THEIR_NEED.topWeight / Math.max(1, world.topWeight)), rim),
          topWeight: world.topWeight,
          scale: THEIR_NEED.scale,
        },
        sharedAtLeast: world.sharedAtLeast,
        traditionsOfKey: world.traditionsOfKey,
        palette: world.palette,
        squash: world.squash,
      },
      /*
       * HOW FAST THE NAMES COME UP — and a deviation that turned out to be compensating for a typo.
       *
       * This read: "their fade is (zoom - 0.02) / 0.20, and at their whole-field zoom it comes to
       * EIGHT PER CENT" — so the names were made to come up over a doubling instead, because eight
       * per cent of the only layer we draw is a dead star.
       *
       * Their fade is `(z - 0.012) / 0.02` (engine.js:1566). The span was ten times over, and the
       * eight per cent it implied was never true of their map: at their whole-field zoom the real
       * expression clamps to ONE. Their resident names are at full strength out there, and the
       * 9,700 sky dots are drawn beside them rather than instead of them.
       *
       * So there is nothing to compensate for. Their numbers, rescaled, put ours at full strength
       * at our own whole-field zoom too — the deviation and the architectural note it pointed at
       * were both answers to a number nobody had checked.
       */
      zooms: {
        from: atOurRim(THEIR_NODE_ZOOMS.from, rim),
        span: atOurRim(THEIR_NODE_ZOOMS.span, rim),
      },
      paint: {
        palette: world.palette,
        sharedAtLeast: world.sharedAtLeast,
        traditionsOfKey: world.traditionsOfKey,
        shapeOf: kind => shapeFor(kind),
        haloWeight: world.topWeight,
        corpusOrder: world.corpusKeys,
        off: {},
      },
    },
    wedges: {
      paint: {
        wedges: world.wedges.map(w => ({ ...w, label: words.wedgeLabel(world.corpusNames[w.k] ?? '') })),
        palette: world.palette,
        countLine: words.countLine,
        squash: world.squash,
      },
      zooms: {
        until: atOurRim(THEIR_WEDGE_ZOOMS.until, rim),
        span: atOurRim(THEIR_WEDGE_ZOOMS.span, rim),
      },
    },
    /*
     * EVERY ONE OF THESE IS THEIRS, RESCALED — and none of them used to be.
     *
     * This block was written from nothing while the values the engine actually states sat thirty
     * lines away in `node-labels.test.ts`, where they were checked against it. `from` was 0.09,
     * which appears nowhere in their engine; the zoom bands were a geometric series where theirs is
     * an irregular ladder; and the weight gate ran 1,2,3,4,5, ASCENDING, where theirs runs 7,5,4,3,0.
     *
     * That last one is the far view. Their gate asks for seven ties before a name is written across
     * the whole field and relaxes to nothing as the reader comes in; ours asked for one far out and
     * five close in — backwards at both ends. It is why our whole-field view carried ninety names,
     * "Joseph's Egyptian master (Potiphar / al-Aziz)" among them, and theirs carries none.
     *
     * They now come from `node-labels.ts`, which is also what the test reads, so there is one copy.
     */
    labels: {
      zooms: {
        from: atOurRim(THEIR_LABEL_ZOOMS.from, rim),
        budgetSteps: [
          atOurRim(THEIR_LABEL_ZOOMS.budgetSteps[0], rim),
          atOurRim(THEIR_LABEL_ZOOMS.budgetSteps[1], rim),
        ],
        weightSteps: [
          atOurRim(THEIR_LABEL_ZOOMS.weightSteps[0], rim),
          atOurRim(THEIR_LABEL_ZOOMS.weightSteps[1], rim),
          atOurRim(THEIR_LABEL_ZOOMS.weightSteps[2], rim),
          atOurRim(THEIR_LABEL_ZOOMS.weightSteps[3], rim),
        ],
        revealStep: atOurRim(THEIR_LABEL_ZOOMS.revealStep, rim),
        ignoreWeightAbove: atOurRim(THEIR_LABEL_ZOOMS.ignoreWeightAbove, rim),
      },
      weights: labelWeightsFor(world.topWeight),
    },
    arrival: {
      floor: atOurRim(THEIR_ARRIVAL.floor, rim),
      perVisited: atOurRim(THEIR_ARRIVAL.perVisited, rim),
      floorMin: atOurRim(THEIR_ARRIVAL.floorMin, rim),
      arriveAt: z('arriveAt'),
      lookAt: z('lookAt'),
    },
    floors: {
      held: z('floorHeld'),
      faintest: z('floorFaintest'),
      mapBase: z('floorMapBase'),
      perVisited: z('floorPerVisited'),
    },
    rim,
  };
}

/** The moving frame's scene. */
export function ourLiveScene(world: OurWorld): LiveScene {
  const rim = world.rim;
  const z = (k: keyof typeof THEIR_ZOOMS): number => atOurRim(THEIR_ZOOMS[k], rim);
  return {
    zooms: {
      travellersFrom: z('travellersFrom'),
      presenceFrom: z('presenceFrom'),
      disputedFrom: z('disputedFrom'),
      nearFrom: z('nearFrom'),
      nearSpan: z('nearSpan'),
    },
    presence: { seatCap: 8 },
    palette: world.palette,
    /* Nothing in our roster is the kind their `x` is — a name no scripture agrees exists. The
       engine wants one to exclude, and a kind we never emit is the honest way to exclude nothing. */
    absentKind: '\u0000',
    dust: ourNearDust(),
    /*
     * `localTie` IS THE FRAME'S OWN WIDTH IN WORLD UNITS at the zoom the passages start appearing.
     *
     * A passage is written on its strand, so a strand shorter than the frame has its middle in view
     * and theirs' placement works — that is what "local" means here, and it is the quantity the
     * question is actually about. Anything longer has its middle off screen by construction, however
     * the camera is pointed, and is the case their design never meets: their ties are built inside a
     * region and never approach a frame's width.
     *
     * A share of the rim, like every other threshold here, and the share is measured rather than
     * picked: the frame at `fanRefsFrom` is 4.9% of the rim across (`open-fan.ts` records the sweep).
     * Our roster's median tie is 27% of the rim and Abraham's is 32%, so ours are over it and their
     * local ties would be far under it.
     *
     * The viewport is not in scope here and that is the one imprecision: a very wide window frames
     * more world than 4.9% and a few ties would then take the reach when their middles were in view
     * after all. The reach returns the middle whenever the middle is on screen, so that costs
     * nothing — it is the same point by a longer route.
     */
    fan: { zooms: { refsFrom: z('fanRefsFrom'), localTie: rim * FRAME_SHARE_AT_REFS } },
    chips: { zooms: { from: z('chipsFrom') } },
  };
}

/** Our shapes, in the vocabulary `nodes-draw.ts` speaks. */
function shapeFor(kind: string): 'absent' | 'hollow' | 'eyed' | 'square' | 'diamond' | 'disc' {
  const shape = SHAPE_BY_TYPE[kind];
  if (shape === 'square') return 'square';
  if (shape === 'diamond') return 'diamond';
  return 'disc';
}

/** The reader's own view, as the layers that read it want it. */
export function ourLook(): OurLook {
  return { starDensity: 1, labelDensity: 1, off: {}, chrome: [] };
}

/**
 * THE CITATIONS, TRANSLATED FROM NAMES TO THE NUMBERS THE ENGINE COUNTS FIGURES BY.
 *
 * Callers hold ids — `moses|sinai`, as `core/map-model.ts`'s `tieRefs` builds them — because the
 * index is this adapter's private business, and a caller that had to know it would be a second copy
 * of the roster's ordering kept in step by hand. The fan asks by index, keyed `min:max`, exactly as
 * their `edgeRef` is; both keys are SORTED so a tie answers from whichever end the reader opened.
 *
 * A pair naming a figure this map does not draw is dropped rather than kept under a broken key: the
 * roster the reader is looking at can be a subset (a tile that has not landed, a figure below the
 * tier being shown), and a citation for a strand that is not on the screen is not a citation.
 */
export function indexedRefs(
  byId: ReadonlyMap<string, string>,
  indexOf: ReadonlyMap<string, number>,
): Map<string, string> {
  const refs = new Map<string, string>();
  for (const [pair, ref] of byId) {
    const [a, b] = pair.split('|');
    /* `a` can never be undefined — a split yields at least one element, `''.split('|')` included —
       so that arm is dead, kept only because `noUncheckedIndexedAccess` cannot know it. `b` is a
       live branch: a key with no separator at all reaches here and must be dropped, not crash. */
    const i = a === undefined ? undefined : indexOf.get(a);
    const j = b === undefined ? undefined : indexOf.get(b);
    if (i === undefined || j === undefined) continue;
    /* The fan's OWN key function, not a second spelling of it — `open-fan.ts` exports it precisely
       because a second answer to "are these the same tie" is how the citations go silently missing. */
    refs.set(pairKey(i, j), ref);
  }
  return refs;
}
